import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import AppShell from "../components/AppShell.jsx";
import MapboxFieldView from "../components/MapboxFieldView.jsx";
import FieldShapeIcon from "../components/FieldShapeIcon.jsx";
import FieldInfoPanel from "../components/FieldInfoPanel.jsx";
import { apiFetch, getStoredUser, safeReadError } from "../api/api.js";

import "mapbox-gl/dist/mapbox-gl.css";
import "@mapbox/mapbox-gl-draw/dist/mapbox-gl-draw.css";

function readTheme() {
  try {
    return document.documentElement.getAttribute("data-theme") || "light";
  } catch {
    return "light";
  }
}

export default function Main() {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [user, setUser] = useState(null);
  const [groups, setGroups] = useState([]);
  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);

  const [msg, setMsg] = useState(null);

  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [flyToKey, setFlyToKey] = useState(null);

  const listRef = useRef(null);

  const [theme, setTheme] = useState(readTheme);
  const isDark = theme === "dark";

  useEffect(() => {
    const root = document.documentElement;

    const syncTheme = () => {
      setTheme(root.getAttribute("data-theme") || "light");
    };

    syncTheme();

    const observer = new MutationObserver(syncTheme);
    observer.observe(root, {
      attributes: true,
      attributeFilter: ["data-theme", "class"]
    });

    return () => observer.disconnect();
  }, []);

  const [weather, setWeather] = useState(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [weatherError, setWeatherError] = useState(null);
  const [weatherCollapsed, setWeatherCollapsed] = useState(true);

  const lastWeatherKeyRef = useRef(null);

  const [infoOpen, setInfoOpen] = useState(false);
  const [infoCollapsed, setInfoCollapsed] = useState(false);

  const toastTimerRef = useRef(null);

  const toast = useCallback((type, text) => {
    setMsg({ type, text });
    if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    toastTimerRef.current = window.setTimeout(() => setMsg(null), 2400);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimerRef.current) window.clearTimeout(toastTimerRef.current);
    };
  }, []);

  useEffect(() => {
    const u = getStoredUser();
    if (!u) {
      navigate("/login");
      return;
    }
    setUser(u);
  }, [navigate]);

  const load = useCallback(async () => {
    if (!user) return;

    setLoading(true);
    try {
      const resG = await apiFetch(user, "/api/field-groups", { method: "GET" }, navigate);
      if (!resG.ok) {
        const e = await safeReadError(resG);
        toast("err", e || `Помилка груп (${resG.status})`);
        setGroups([]);
      } else {
        const gArr = (await resG.json()) || [];
        const arrG = Array.isArray(gArr) ? gArr : [];
        arrG.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "uk"));
        setGroups(arrG);
      }

      const resF = await apiFetch(user, "/api/fields", { method: "GET" }, navigate);
      if (!resF.ok) {
        const e = await safeReadError(resF);
        toast("err", e || `Помилка полів (${resF.status})`);
        setFields([]);
      } else {
        const fArr = (await resF.json()) || [];
        const arr = Array.isArray(fArr) ? fArr : [];
        arr.sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || ""), "uk"));
        setFields(arr);

        if (!selectedFieldId) {
          const firstWithGeom = arr.find((x) => !!x.geometry);
          if (firstWithGeom) {
            const id = String(firstWithGeom.id);
            setSelectedFieldId(id);
            setFlyToKey(`${id}|${Date.now()}`);
          }
        }
      }
    } catch {
      toast("err", t("common.error"));
      setGroups([]);
      setFields([]);
    } finally {
      setLoading(false);
    }
  }, [user, navigate, toast, selectedFieldId]);

  useEffect(() => {
    load().catch(() => {});
  }, [load]);

  const totalArea = useMemo(() => {
    return fields.reduce((acc, f) => acc + Number(f?.areaHa || 0), 0);
  }, [fields]);



  const roleText = useMemo(() => {
    switch (String(user?.role || "").toUpperCase()) {
      case "MANAGER":    return "Менеджер";
      case "AGRONOMIST": return "Агроном";
      case "WORKER":     return "Працівник";
      case "ADMIN":      return "Адміністратор";
      default:           return user?.role || "Користувач";
    }
  }, [user]);

  const orgName = useMemo(() => {
    return user?.organizationName || user?.organization?.name || "Організація";
  }, [user]);

  const firstLetter = useMemo(() => {
    return String(user?.name || "U").trim().slice(0, 1).toUpperCase();
  }, [user]);

  useEffect(() => {
    if (!selectedFieldId) return;
    const root = listRef.current;
    if (!root) return;

    const el = root.querySelector(`[data-field-id="${selectedFieldId}"]`);
    if (!el) return;

    try {
      el.scrollIntoView({ block: "nearest", behavior: "smooth" });
    } catch {}
  }, [selectedFieldId]);

  const handleSelectFromMap = useCallback((id) => {
    const sid = String(id);
    setSelectedFieldId(sid);
    setFlyToKey(`${sid}|${Date.now()}`);
  }, []);

  const handleSelectFromList = useCallback((id) => {
    const sid = String(id);
    setSelectedFieldId(sid);
    setFlyToKey(`${sid}|${Date.now()}`);
  }, []);

  const flyToId = useMemo(() => {
    if (!flyToKey) return null;
    return String(flyToKey).split("|")[0];
  }, [flyToKey]);

  const selectedField = useMemo(() => {
    if (!selectedFieldId) return null;
    return fields.find((f) => String(f?.id) === String(selectedFieldId)) || null;
  }, [fields, selectedFieldId]);

  const computeCenterFromBbox = useCallback((f) => {
    const minLat = f?.bboxMinLat;
    const maxLat = f?.bboxMaxLat;
    const minLng = f?.bboxMinLng;
    const maxLng = f?.bboxMaxLng;

    if (
      minLat == null ||
      maxLat == null ||
      minLng == null ||
      maxLng == null ||
      !isFinite(Number(minLat)) ||
      !isFinite(Number(maxLat)) ||
      !isFinite(Number(minLng)) ||
      !isFinite(Number(maxLng))
    ) {
      return null;
    }

    const lat = (Number(minLat) + Number(maxLat)) / 2;
    const lon = (Number(minLng) + Number(maxLng)) / 2;
    return { lat, lon };
  }, []);

  const loadWeatherForField = useCallback(
    async (f) => {
      if (!user || !f) return;

      const center = computeCenterFromBbox(f);
      if (!center) {
        setWeather(null);
        setWeatherError(t("common.error"));
        setWeatherLoading(false);
        return;
      }

      const key = `${f.id}|${center.lat.toFixed(5)}|${center.lon.toFixed(5)}`;
      if (lastWeatherKeyRef.current === key) return;
      lastWeatherKeyRef.current = key;

      setWeatherLoading(true);
      setWeatherError(null);

      try {
        const res = await apiFetch(
          user,
          "/api/weather",
          {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              lat: center.lat,
              lon: center.lon,
              units: "metric",
              lang: "uk"
            })
          },
          navigate
        );

        if (!res.ok) {
          const e = await safeReadError(res);
          setWeather(null);
          setWeatherError(e || `Помилка погоди (${res.status})`);
          return;
        }

        const data = await res.json();
        setWeather(data || null);
      } catch {
        setWeather(null);
        setWeatherError(t("common.error"));
      } finally {
        setWeatherLoading(false);
      }
    },
    [user, computeCenterFromBbox, navigate]
  );

  useEffect(() => {
    if (!selectedField) {
      setWeather(null);
      setWeatherError(null);
      return;
    }
    loadWeatherForField(selectedField).catch(() => {});
  }, [selectedField, loadWeatherForField]);

  const prettyTemp = useMemo(() => {
    const t = Number(weather?.temperature);
    if (!isFinite(t)) return null;
    return Math.round(t);
  }, [weather]);

  const prettyWind = useMemo(() => {
    const w = Number(weather?.windSpeed);
    if (!isFinite(w)) return null;
    return w.toFixed(1);
  }, [weather]);

  const prettyHum = useMemo(() => {
    const h = Number(weather?.humidity);
    if (!isFinite(h)) return null;
    return Math.round(h);
  }, [weather]);

  const prettyDesc = useMemo(() => {
    const d = String(weather?.description || "").trim();
    if (!d) return null;
    return d.charAt(0).toUpperCase() + d.slice(1);
  }, [weather]);

  const iconUrl = useMemo(() => {
    const ic = String(weather?.icon || "").trim();
    if (!ic) return null;
    return `https://openweathermap.org/img/wn/${ic}@2x.png`;
  }, [weather]);

  const groupOfSelected = useMemo(() => {
    if (!selectedField) return null;
    const gid = selectedField?.groupId ?? selectedField?.fieldGroupId ?? selectedField?.group?.id;
    if (gid == null) return null;
    return groups.find((g) => String(g.id) === String(gid)) || null;
  }, [selectedField, groups]);

  const styles = {
    topActions: {
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
      alignItems: "center"
    },

    topGhostBtn: {
      padding: "11px 14px",
      borderRadius: 14,
      border: "1px solid var(--border-strong, var(--border))",
      background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
      color: "var(--text)",
      fontWeight: 950,
      cursor: "pointer",
      boxShadow: isDark ? "0 10px 24px rgba(0,0,0,0.16)" : "0 10px 22px rgba(15,23,42,0.08)"
    },

    topPrimaryBtn: {
      padding: "11px 14px",
      borderRadius: 14,
      border: isDark ? "1px solid rgba(34,197,94,0.40)" : "1px solid rgba(22,163,74,0.30)",
      background: isDark ? "rgba(34,197,94,0.14)" : "linear-gradient(180deg, #22c55e, #16a34a)",
      color: isDark ? "var(--text)" : "#ffffff",
      fontWeight: 950,
      cursor: "pointer",
      boxShadow: isDark ? "none" : "0 14px 26px rgba(22,163,74,0.24)"
    },

    toastWrap: {
      position: "fixed",
      right: 16,
      top: 16,
      zIndex: 200,
      padding: "11px 13px",
      borderRadius: 14,
      border: `1px solid ${
        msg?.type === "ok" ? "rgba(34,197,94,0.38)" : "rgba(239,71,111,0.32)"
      }`,
      background: "var(--panel2)",
      backdropFilter: "blur(14px)",
      boxShadow: "var(--shadow-strong, var(--shadow))",
      color: "var(--text)",
      maxWidth: 520
    },

    wrap: {
      marginTop: 14,
      display: "grid",
      gap: 14,
      paddingRight: 0
    },

    hero: {
      padding: 18,
      borderRadius: 20,
      border: "1px solid var(--border)",
      background: isDark
        ? "radial-gradient(1000px 420px at 0% 0%, rgba(34,197,94,0.12), transparent 60%), var(--panel)"
        : "linear-gradient(180deg, #ffffff, #f8fafc)",
      boxShadow: isDark ? "var(--shadow)" : "0 20px 46px rgba(15,23,42,0.10)",
      display: "flex",
      justifyContent: "space-between",
      alignItems: "center",
      gap: 16,
      flexWrap: "wrap",
      backdropFilter: "blur(12px)"
    },

    heroLeft: {
      display: "flex",
      alignItems: "center",
      gap: 14,
      flexWrap: "wrap"
    },

    avatar: {
      width: 58,
      height: 58,
      borderRadius: 20,
      display: "grid",
      placeItems: "center",
      background: isDark
        ? "rgba(34,197,94,0.18)"
        : "linear-gradient(180deg, rgba(34,197,94,0.18), rgba(22,163,74,0.12))",
      border: "1px solid rgba(34,197,94,0.24)",
      fontWeight: 950,
      fontSize: 18,
      color: "var(--text)",
      flex: "0 0 auto",
      boxShadow: isDark ? "none" : "0 12px 24px rgba(22,163,74,0.10)"
    },

    name: {
      fontWeight: 950,
      fontSize: 20,
      letterSpacing: 0.2,
      color: "var(--text)"
    },

    sub: {
      opacity: 0.92,
      fontWeight: 800,
      marginTop: 4,
      color: "var(--muted)"
    },

    kpis: {
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
      alignItems: "center"
    },

    pill: {
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      padding: "11px 14px",
      borderRadius: 999,
      border: "1px solid var(--border)",
      background: isDark ? "var(--surface)" : "#ffffff",
      fontWeight: 900,
      color: "var(--text)",
      whiteSpace: "nowrap",
      boxShadow: isDark ? "0 6px 20px rgba(0,0,0,0.08)" : "0 10px 24px rgba(15,23,42,0.06)"
    },

    card: {
      borderRadius: 20,
      border: "1px solid var(--border)",
      background: isDark ? "var(--panel)" : "linear-gradient(180deg, #ffffff, #f8fafc)",
      boxShadow: isDark ? "var(--shadow)" : "0 18px 42px rgba(15,23,42,0.08)",
      overflow: "hidden",
      backdropFilter: "blur(12px)"
    },

    cardHead: {
      padding: "14px 16px",
      borderBottom: "1px solid var(--border)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      flexWrap: "wrap",
      background: isDark ? "transparent" : "rgba(255,255,255,0.55)"
    },

    cardTitle: {
      fontWeight: 950,
      letterSpacing: 0.2,
      color: "var(--text)",
      fontSize: 15
    },

    cardBody: {
      padding: "14px 16px"
    },

    sectionGhostBtn: {
      padding: "10px 13px",
      borderRadius: 14,
      border: "1px solid var(--border-strong, var(--border))",
      background: isDark ? "rgba(255,255,255,0.05)" : "#ffffff",
      color: "var(--text)",
      fontWeight: 950,
      cursor: "pointer",
      boxShadow: isDark ? "0 8px 18px rgba(0,0,0,0.14)" : "0 10px 20px rgba(15,23,42,0.06)"
    },

    groupsSummary: {
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
      marginBottom: 12
    },

    groupsListWrap: {
      borderRadius: 18,
      border: "1px solid var(--border)",
      background: isDark ? "var(--surface)" : "rgba(255,255,255,0.78)",
      overflow: "hidden",
      boxShadow: isDark ? "0 10px 24px rgba(0,0,0,0.14)" : "0 10px 24px rgba(15,23,42,0.05)"
    },

    groupsListHead: {
      padding: "12px 14px",
      borderBottom: "1px solid var(--border)",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      background: isDark ? "rgba(255,255,255,0.02)" : "rgba(248,250,252,0.95)",
      flexWrap: "wrap"
    },

    groupsListTitle: {
      fontWeight: 950,
      color: "var(--text)"
    },

    groupsListMeta: {
      fontWeight: 900,
      color: "var(--muted)"
    },

    groupsList: {
      maxHeight: 520,
      overflowY: "auto",
      overflowX: "hidden",
      padding: 10,
      display: "grid",
      gap: 10,
      scrollbarWidth: "thin"
    },

    groupRowBtn: {
      appearance: "none",
      WebkitAppearance: "none",
      border: 0,
      padding: 0,
      margin: 0,
      width: "100%",
      background: "transparent",
      textAlign: "left",
      cursor: "pointer",
      color: "var(--text)",
      font: "inherit",
      display: "block"
    },

    groupRowCard: {
      borderRadius: 18,
      border: "1px solid var(--border)",
      background: isDark
        ? "radial-gradient(900px 360px at 0% 0%, rgba(34,197,94,0.10), transparent 60%), var(--surface)"
        : "#ffffff",
      boxShadow: isDark ? "0 14px 34px rgba(0,0,0,0.16)" : "0 10px 24px rgba(15,23,42,0.06)",
      padding: 14,
      transition: "transform .12s ease, border-color .12s ease, box-shadow .12s ease",
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 14,
      flexWrap: "wrap"
    },

    groupRowLeft: {
      minWidth: 0,
      display: "flex",
      flexDirection: "column",
      gap: 8,
      flex: "1 1 auto"
    },

    groupTop: {
      display: "flex",
      alignItems: "flex-start",
      justifyContent: "space-between",
      gap: 10,
      flexWrap: "wrap"
    },

    groupName: {
      fontWeight: 950,
      fontSize: 16,
      color: "var(--text)",
      lineHeight: 1.2,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },

    groupMeta: {
      display: "flex",
      gap: 10,
      flexWrap: "wrap",
      opacity: 0.98
    },

    badgeOk: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
      borderRadius: 999,
      border: isDark ? "1px solid rgba(34,197,94,0.24)" : "1px solid #bbf7d0",
      background: isDark ? "rgba(34,197,94,0.10)" : "#ecfdf5",
      fontWeight: 950,
      fontSize: 13,
      whiteSpace: "nowrap",
      color: isDark ? "rgba(235,255,242,0.95)" : "#065f46"
    },

    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      padding: "8px 10px",
      borderRadius: 999,
      border: "1px solid var(--border)",
      background: isDark ? "var(--surface)" : "#ffffff",
      fontWeight: 900,
      fontSize: 13,
      whiteSpace: "nowrap",
      color: "var(--text)",
      boxShadow: isDark ? "none" : "0 8px 18px rgba(15,23,42,0.04)"
    },

    groupNote: {
      marginTop: 2,
      opacity: 0.92,
      lineHeight: 1.35,
      color: "var(--muted)",
      display: "-webkit-box",
      WebkitLineClamp: 2,
      WebkitBoxOrient: "vertical",
      overflow: "hidden"
    },

    groupRowRight: {
      flex: "0 0 auto",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      minWidth: 90
    },

    groupCta: {
      fontWeight: 950,
      color: isDark ? "var(--text)" : "#0f7a39",
      whiteSpace: "nowrap"
    },

    mapRow: {
      display: "grid",
      gridTemplateColumns: "1fr 360px",
      gap: 12,
      padding: 12,
      alignItems: "stretch"
    },

    mapWrap: {
      position: "relative",
      borderRadius: 18,
      overflow: "hidden",
      minHeight: 520,
      border: "1px solid var(--border)",
      boxShadow: isDark ? "none" : "0 14px 30px rgba(15,23,42,0.05)"
    },

    weatherBox: {
      position: "absolute",
      left: 12,
      top: 12,
      zIndex: 60,
      width: "clamp(300px, 42vw, 380px)",
      maxWidth: "calc(100% - 24px)",
      borderRadius: 18,
      border: "1px solid var(--border)",
      background: isDark
        ? "radial-gradient(800px 260px at 0% 0%, rgba(34,197,94,0.18), transparent 55%), rgba(0,0,0,0.45)"
        : "#ffffff",
      backdropFilter: "blur(14px)",
      boxShadow: isDark ? "0 18px 50px rgba(0,0,0,0.35)" : "0 18px 40px rgba(15,23,42,0.10)",
      padding: 12,
      color: "var(--text)",
      boxSizing: "border-box"
    },

    weatherMini: {
      position: "absolute",
      left: 12,
      top: 12,
      zIndex: 70,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      padding: "10px 14px",
      width: "clamp(320px, 34vw, 420px)",
      maxWidth: "calc(100% - 24px)",
      minHeight: 56,
      borderRadius: 999,
      border: "1px solid var(--border)",
      background: isDark ? "rgba(0,0,0,0.62)" : "rgba(255,255,255,0.97)",
      backdropFilter: "blur(14px)",
      boxShadow: isDark ? "0 18px 50px rgba(0,0,0,0.32)" : "0 14px 34px rgba(15,23,42,0.10)",
      color: "var(--text)",
      overflow: "hidden",
      boxSizing: "border-box"
    },

    miniMain: {
      display: "inline-flex",
      alignItems: "center",
      gap: 10,
      minWidth: 0,
      flex: "1 1 auto"
    },

    miniText: {
      fontWeight: 950,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minWidth: 0
    },

    miniEye: {
      flex: "0 0 auto",
      opacity: 0.86,
      fontWeight: 950
    },

    weatherTop: {
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      flexWrap: "wrap"
    },

    weatherTitle: {
      fontWeight: 950,
      letterSpacing: 0.2,
      fontSize: 13,
      opacity: 0.9,
      minWidth: 0,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },

    weatherBtnRow: {
      display: "flex",
      gap: 8,
      alignItems: "center",
      flex: "0 0 auto"
    },

    weatherIconBtn: {
      flex: "0 0 auto",
      width: 40,
      height: 40,
      borderRadius: 14,
      display: "grid",
      placeItems: "center",
      fontWeight: 950,
      border: "1px solid var(--border)",
      background: isDark ? "var(--surface)" : "#ffffff",
      color: "var(--text)",
      cursor: "pointer",
      lineHeight: 1,
      padding: 0,
      boxShadow: isDark ? "none" : "0 8px 18px rgba(15,23,42,0.05)"
    },

    weatherField: {
      marginTop: 6,
      fontWeight: 950,
      fontSize: 14,
      lineHeight: 1.15,
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap"
    },

    weatherMain: {
      marginTop: 10,
      display: "grid",
      gridTemplateColumns: "1fr 1fr",
      gap: 10,
      alignItems: "center"
    },

    weatherTemp: {
      fontSize: 36,
      fontWeight: 950,
      lineHeight: 1,
      color: "var(--text)"
    },

    weatherDesc: {
      marginTop: 4,
      fontWeight: 900,
      opacity: 0.88,
      fontSize: 13
    },

    weatherGrid: {
      marginTop: 10,
      display: "grid",
      gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
      gap: 8,
      width: "100%",
      boxSizing: "border-box"
    },

    wPill: {
      padding: "9px 10px",
      borderRadius: 14,
      border: "1px solid var(--border)",
      background: isDark ? "var(--surface)" : "#ffffff",
      fontWeight: 900,
      fontSize: 12,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      gap: 10,
      minWidth: 0,
      width: "100%",
      overflow: "hidden",
      boxSizing: "border-box",
      boxShadow: isDark ? "none" : "0 8px 18px rgba(15,23,42,0.04)"
    },

    wLeft: {
      display: "inline-flex",
      alignItems: "center",
      gap: 8,
      minWidth: 0,
      flex: "1 1 auto",
      overflow: "hidden"
    },

    wLabel: {
      opacity: 0.82,
      fontWeight: 950,
      whiteSpace: "nowrap",
      overflow: "hidden",
      textOverflow: "ellipsis",
      minWidth: 0
    },

    wValue: {
      fontWeight: 950,
      whiteSpace: "nowrap",
      opacity: 0.95,
      flex: "0 0 auto",
      maxWidth: "52%",
      overflow: "hidden",
      textOverflow: "ellipsis"
    },

    listBox: {
      borderRadius: 18,
      border: "1px solid var(--border)",
      background: isDark ? "var(--panel)" : "linear-gradient(180deg, #ffffff, #f8fafc)",
      overflow: "hidden",
      display: "flex",
      flexDirection: "column",
      height: 520,
      minHeight: 520,
      backdropFilter: "blur(12px)",
      boxShadow: isDark ? "none" : "0 14px 30px rgba(15,23,42,0.06)"
    },

    listHead: {
      padding: "12px 14px",
      borderBottom: "1px solid var(--border)",
      display: "flex",
      justifyContent: "space-between",
      gap: 10,
      alignItems: "center",
      flex: "0 0 auto",
      background: isDark ? "transparent" : "rgba(255,255,255,0.55)",
      flexWrap: "wrap"
    },

    listTitle: {
      fontWeight: 950,
      opacity: 0.95,
      color: "var(--text)"
    },

    listBody: {
      flex: "1 1 auto",
      minHeight: 0,
      overflowY: "auto",
      overflowX: "hidden",
      padding: 10,
      display: "grid",
      gap: 10,
      overscrollBehavior: "contain",
      scrollbarWidth: "thin"
    },

    fieldItem: (active) => ({
      display: "flex",
      gap: 10,
      alignItems: "center",
      padding: 11,
      borderRadius: 16,
      border: active ? "1px solid rgba(34,197,94,0.35)" : "1px solid var(--border)",
      background: active
        ? isDark
          ? "radial-gradient(600px 220px at 0% 0%, rgba(34,197,94,0.12), transparent 55%), var(--surface-soft)"
          : "linear-gradient(180deg, rgba(34,197,94,0.10), rgba(255,255,255,0.98))"
        : isDark
          ? "var(--surface)"
          : "#ffffff",
      cursor: "pointer",
      boxShadow: active
        ? isDark
          ? "0 10px 24px rgba(0,0,0,0.16)"
          : "0 12px 26px rgba(22,163,74,0.10)"
        : !isDark
          ? "0 8px 18px rgba(15,23,42,0.04)"
          : "none",
      flexWrap: "wrap"
    }),

    fieldName: {
      fontWeight: 950,
      color: "var(--text)"
    },

    fieldArea: {
      marginLeft: "auto",
      padding: "8px 10px",
      borderRadius: 999,
      border: "1px solid var(--border)",
      background: isDark ? "var(--surface-soft)" : "#ffffff",
      fontWeight: 950,
      whiteSpace: "nowrap",
      color: "var(--text)"
    }
  };

  return (
    <AppShell
      title={t("main.title")}
      right={
        <div style={styles.topActions}>
          <button style={styles.topGhostBtn} onClick={() => navigate("/fields/groups")} type="button">
            {t("main.openGroups")}
          </button>
          <button style={styles.topPrimaryBtn} onClick={() => navigate("/fields")} type="button">
            {t("main.openFields")}
          </button>
        </div>
      }
    >
      {msg && (
        <div style={styles.toastWrap}>
          <span style={{ fontWeight: 950 }}>{msg.text}</span>
        </div>
      )}

      <div style={styles.wrap}>
        <div style={styles.hero}>
          <div style={styles.heroLeft}>
            <div style={styles.avatar}>{firstLetter}</div>
            <div>
              <div style={styles.name}>{user?.name || t("common.noName")}</div>
              <div style={styles.sub}>
                {orgName} · <span style={{ opacity: 0.92 }}>{roleText}</span>
              </div>
              {loading ? (
                <div style={{ marginTop: 7, opacity: 0.85, fontWeight: 900, color: "var(--muted)" }}>
                  {t("common.loadingData")}
                </div>
              ) : null}
            </div>
          </div>

          <div style={styles.kpis}>
            <div style={styles.pill}>📦 {t("main.kpi.groups")}: {groups.length}</div>
            <div style={styles.pill}>🌿 {t("main.kpi.fields")}: {fields.length}</div>
            <div style={styles.pill}>📏 {t("main.kpi.total")}: {totalArea.toFixed(1)} {t("common.ha")}</div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHead}>
            <div style={styles.cardTitle}>{t("main.mapTitle")}</div>
            <button type="button" style={styles.sectionGhostBtn} onClick={() => navigate("/fields")}>
              {t("main.openFieldsBtn")}
            </button>
          </div>

          <div style={styles.mapRow} className="mapRow">
            <div style={styles.mapWrap} className="mapWrap">
              {infoOpen ? (
                <FieldInfoPanel
                  field={selectedField}
                  group={groupOfSelected}
                  collapsed={infoCollapsed}
                  onCollapse={() => setInfoCollapsed(true)}
                  onExpand={() => setInfoCollapsed(false)}
                  onClose={() => setInfoOpen(false)}
                  onOpenDetail={() => {
                    if (selectedField?.id != null) navigate(`/fields/${selectedField.id}`);
                  }}
                  onOpenTasks={() => {
                    if (selectedField?.id != null) navigate(`/tasks?fieldId=${selectedField.id}`);
                  }}
                />
              ) : (
                <button
                  type="button"
                  className="infoMini"
                  onClick={() => {
                    setInfoOpen(true);
                    setInfoCollapsed(false);
                  }}
                  title={t("main.showFieldInfo")}
                >
                  ℹ️ <span style={{ opacity: 0.92, fontWeight: 950 }}>{t("common.field")}</span>
                </button>
              )}

              {weatherCollapsed ? (
                <button
                  type="button"
                  style={styles.weatherMini}
                  className="weatherMini"
                  onClick={() => setWeatherCollapsed(false)}
                  title={t("main.weather.show")}
                >
                  <span style={styles.miniMain}>
                    <span style={{ fontWeight: 950 }}>☁️</span>
                    <span style={styles.miniText}>
                      {prettyTemp != null ? `${prettyTemp}°` : "Погода"}
                      {selectedField?.name ? ` · ${selectedField.name}` : ""}
                    </span>
                  </span>

                  <span style={styles.miniEye}>👁</span>
                </button>
              ) : (
                <div style={styles.weatherBox} className="weatherBox">
                  <div style={styles.weatherTop}>
                    <div style={styles.weatherTitle}>{t("main.weather.title")}</div>

                    <div style={styles.weatherBtnRow}>
                      <button
                        type="button"
                        style={styles.weatherIconBtn}
                        onClick={() => setWeatherCollapsed(true)}
                        title={t("common.hide")}
                      >
                        🙈
                      </button>

                      <button
                        type="button"
                        style={styles.weatherIconBtn}
                        onClick={() => {
                          if (selectedField) {
                            lastWeatherKeyRef.current = null;
                            loadWeatherForField(selectedField);
                          }
                        }}
                        disabled={weatherLoading}
                        title={t("common.refresh")}
                      >
                        {weatherLoading ? "…" : "↻"}
                      </button>
                    </div>
                  </div>

                  <div style={styles.weatherField} title={selectedField?.name || ""}>
                    {selectedField?.name ? selectedField.name : t("main.selectField")}
                  </div>

                  {weatherLoading ? (
                    <div style={{ marginTop: 12, opacity: 0.82, fontWeight: 900 }}>{t("main.weather.loading")}</div>
                  ) : weatherError ? (
                    <div style={{ marginTop: 12, opacity: 0.95, fontWeight: 900, color: "var(--danger)" }}>
                      {weatherError}
                    </div>
                  ) : weather ? (
                    <>
                      <div style={styles.weatherMain}>
                        <div>
                          <div style={styles.weatherTemp} className="weatherTemp">
                            {prettyTemp != null ? `${prettyTemp}°` : "—"}
                          </div>
                          <div style={styles.weatherDesc}>{prettyDesc || "—"}</div>
                        </div>

                        <div style={{ display: "flex", justifyContent: "flex-end" }}>
                          {iconUrl ? (
                            <img
                              src={iconUrl}
                              alt={prettyDesc || "weather"}
                              style={{
                                width: 64,
                                height: 64,
                                filter: isDark
                                  ? "drop-shadow(0 10px 18px rgba(0,0,0,0.35))"
                                  : "drop-shadow(0 10px 18px rgba(15,23,42,0.10))"
                              }}
                            />
                          ) : (
                            <div style={{ opacity: 0.75, fontWeight: 900 }}>☁️</div>
                          )}
                        </div>
                      </div>

                      <div style={styles.weatherGrid} className="weatherGrid">
                        <div style={styles.wPill}>
                          <div style={styles.wLeft}>
                            <span>💧</span>
                            <span style={styles.wLabel}>{t("main.weather.humidity")}</span>
                          </div>
                          <span style={styles.wValue}>{prettyHum != null ? `${prettyHum}%` : "—"}</span>
                        </div>

                        <div style={styles.wPill}>
                          <div style={styles.wLeft}>
                            <span>🌬</span>
                            <span style={styles.wLabel}>{t("main.weather.wind")}</span>
                          </div>
                          <span style={styles.wValue}>{prettyWind != null ? `${prettyWind} ${t("main.weather.windUnit")}` : "—"}</span>
                        </div>
                      </div>

                    </>
                  ) : (
                    <div style={{ marginTop: 12, opacity: 0.82, fontWeight: 900 }}>
                      {t("main.weather.hint")}
                    </div>
                  )}
                </div>
              )}

              <MapboxFieldView
                height={520}
                features={fields}
                selectedId={selectedFieldId}
                flyToId={flyToId}
                onSelect={handleSelectFromMap}
                showCenterMarkers
              />
            </div>

            <div style={styles.listBox} className="listBox">
              <div style={styles.listHead}>
                <div style={styles.listTitle}>{t("main.fieldList")}</div>
                <div style={{ opacity: 0.7, fontWeight: 900, color: "var(--muted)" }}>{fields.length}</div>
              </div>

              <div style={styles.listBody} ref={listRef}>
                {fields.map((f) => {
                  const active = String(f.id) === String(selectedFieldId);
                  return (
                    <div
                      key={f.id}
                      data-field-id={String(f.id)}
                      style={styles.fieldItem(active)}
                      onClick={() => handleSelectFromList(f.id)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter" || e.key === " ") {
                          e.preventDefault();
                          handleSelectFromList(f.id);
                        }
                      }}
                    >
                      <FieldShapeIcon geometry={f.geometry} size={54} />
                      <div style={{ minWidth: 0, flex: "1 1 auto" }}>
                        <div style={styles.fieldName}>{f.name || `Поле #${f.id}`}</div>
                      </div>
                      <div style={styles.fieldArea}>{Number(f.areaHa || 0).toFixed(2)} ha</div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>

        <div style={styles.card}>
          <div style={styles.cardHead}>
            <div style={styles.cardTitle}>{t("main.groupsTitle")}</div>
            <button type="button" style={styles.sectionGhostBtn} onClick={() => navigate("/fields/groups")}>
              {t("main.allGroupsBtn")}
            </button>
          </div>

          <div style={styles.cardBody}>
            {groups.length === 0 ? (
              <div style={{ opacity: 0.85, fontWeight: 900, color: "var(--muted)" }}>
                {t("main.noGroups")}
              </div>
            ) : (
              <>
                <div style={styles.groupsSummary}>
                  <span style={styles.badge}>{t("main.groupsSummary.totalGroups")}: {groups.length}</span>
                  <span style={styles.badgeOk}>{t("main.groupsSummary.totalFields")}: {fields.length}</span>
                  <span style={styles.badge}>{t("main.groupsSummary.totalArea")}: {totalArea.toFixed(1)} {t("common.ha")}</span>
                </div>

                <div style={styles.groupsListWrap}>
                  <div style={styles.groupsListHead}>
                    <div style={styles.groupsListTitle}>{t("main.groupsList.title")}</div>
                    <div style={styles.groupsListMeta}>{t("main.groupsList.scroll")}</div>
                  </div>

                  <div style={styles.groupsList} className="groupsList">
                    {groups.map((g) => (
                      <button
                        key={g.id}
                        type="button"
                        style={styles.groupRowBtn}
                        className="groupRowBtn"
                        onClick={() => navigate(`/fields/groups/${g.id}`)}
                      >
                        <div style={styles.groupRowCard} className="groupRowCard">
                          <div style={styles.groupRowLeft}>
                            <div style={styles.groupTop}>
                              <div style={styles.groupName} title={g.name || `Група #${g.id}`}>
                                {g.name || `Група #${g.id}`}
                              </div>
                            </div>

                            <div style={styles.groupMeta}>
                              <span style={styles.badgeOk}>🌿 {Number(g.totalArea || 0).toFixed(1)} ha</span>
                              <span style={styles.badge}>🧩 {g.fieldsCount ?? 0} полів</span>
                            </div>

                            {g.note ? <div style={styles.groupNote}>{g.note}</div> : null}
                          </div>

                          <div style={styles.groupRowRight}>
                            <div style={styles.groupCta}>{t("common.open")}</div>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>

      <style>{`
        html,
        body,
        #root {
          width: 100%;
          max-width: 100%;
          overflow-x: hidden;
        }

        body {
          margin: 0;
          color: var(--text);
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        html {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }

        html::-webkit-scrollbar,
        body::-webkit-scrollbar {
          width: 0 !important;
          height: 0 !important;
          display: none !important;
          background: transparent !important;
        }

        * {
          box-sizing: border-box;
        }

        .groupRowBtn:focus {
          outline: none;
        }

        .groupRowBtn:hover .groupRowCard {
          transform: translateY(-2px);
          border-color: rgba(34,197,94,0.24);
          box-shadow: ${
            isDark
              ? "0 20px 46px rgba(0,0,0,0.24)"
              : "0 16px 34px rgba(15,23,42,0.10)"
          };
        }

        .groupRowBtn:active .groupRowCard {
          transform: translateY(0px) scale(0.995);
        }

        .infoMini {
          position: absolute;
          left: 12px;
          bottom: 12px;
          z-index: 22;
          border-radius: 999px;
          border: 1px solid var(--border);
          background: ${isDark ? "rgba(0,0,0,0.45)" : "rgba(255,255,255,0.96)"};
          backdrop-filter: blur(14px);
          box-shadow: ${
            isDark
              ? "0 18px 50px rgba(0,0,0,0.30)"
              : "0 14px 34px rgba(15,23,42,0.10)"
          };
          padding: 10px 12px;
          color: var(--text);
          font-weight: 950;
          cursor: pointer;
          display: inline-flex;
          align-items: center;
          gap: 8px;
          max-width: calc(100% - 24px);
        }

        .listBody::-webkit-scrollbar,
        .groupsList::-webkit-scrollbar {
          width: 8px;
        }

        .listBody::-webkit-scrollbar-track,
        .groupsList::-webkit-scrollbar-track {
          background: ${isDark ? "rgba(255,255,255,0.03)" : "rgba(15,23,42,0.05)"};
          border-radius: 999px;
        }

        .listBody::-webkit-scrollbar-thumb,
        .groupsList::-webkit-scrollbar-thumb {
          background: ${isDark ? "rgba(255,255,255,0.16)" : "rgba(15,23,42,0.18)"};
          border-radius: 999px;
        }

        .listBody::-webkit-scrollbar-thumb:hover,
        .groupsList::-webkit-scrollbar-thumb:hover {
          background: ${isDark ? "rgba(255,255,255,0.24)" : "rgba(15,23,42,0.28)"};
        }

        /* Mobile optimizations */
        @media (max-width: 980px) {
          .mapRow {
            grid-template-columns: 1fr !important;
          }

          .listBox {
            height: auto !important;
            min-height: auto !important;
            max-height: 420px !important;
          }

          .mapWrap {
            min-height: 520px !important;
          }

          .groupsList {
            max-height: 460px !important;
          }
        }

        @media (max-width: 768px) {
          .hero {
            flex-direction: column;
            align-items: flex-start;
          }

          .heroLeft {
            width: 100%;
          }

          .kpis {
            width: 100%;
          }

          .kpis .pill {
            flex: 1 1 auto;
            justify-content: center;
          }

          .groupsListHead {
            flex-direction: column;
            align-items: flex-start;
          }
        }

        @media (max-width: 640px) {
          .weatherBox {
            width: min(360px, calc(100% - 20px)) !important;
            left: 10px !important;
            top: 10px !important;
            padding: 10px !important;
            border-radius: 16px !important;
            box-sizing: border-box !important;
          }

          .weatherMini {
            left: 10px !important;
            top: 10px !important;
            width: min(340px, calc(100% - 20px)) !important;
            max-width: calc(100% - 20px) !important;
            min-height: 52px !important;
            box-sizing: border-box !important;
          }

          .weatherTemp {
            font-size: 30px !important;
          }

          .weatherMain {
            grid-template-columns: 1fr !important;
            text-align: center;
          }

          .weatherMain > div:last-child {
            justify-content: center !important;
          }

          .infoMini {
            left: 10px !important;
            bottom: 10px !important;
            max-width: calc(100% - 20px) !important;
          }

          .groupRowCard {
            flex-direction: column !important;
            align-items: flex-start !important;
          }

          .groupRowRight {
            width: 100% !important;
            justify-content: flex-start !important;
            min-width: 0 !important;
          }

          .fieldItem {
            flex-wrap: wrap;
          }

          .fieldArea {
            margin-left: 0 !important;
            width: 100%;
            text-align: center;
          }

          .cardHead {
            flex-direction: column;
            align-items: flex-start;
          }

          .cardHead .sectionGhostBtn {
            width: 100%;
            text-align: center;
          }

          .groupsSummary {
            flex-direction: column;
            align-items: stretch;
          }

          .groupsSummary .badge,
          .groupsSummary .badgeOk {
            width: 100%;
            justify-content: center;
          }

          .topActions {
            flex-direction: column;
            width: 100%;
          }

          .topActions button {
            width: 100%;
            justify-content: center;
          }

          .listHead {
            flex-direction: column;
            align-items: flex-start;
          }
        }

        @media (max-width: 420px) {
          .weatherBox {
            width: min(390px, calc(100% - 20px)) !important;
          }

          .weatherGrid {
            grid-template-columns: 1fr !important;
          }

          .weatherMini {
            width: min(300px, calc(100% - 20px)) !important;
          }

          .weatherBtnRow {
            width: 100%;
            justify-content: space-between;
          }

          .weatherIconBtn {
            flex: 1 1 auto;
          }

          .avatar {
            width: 48px;
            height: 48px;
            font-size: 16px;
          }

          .name {
            font-size: 18px;
          }

          .pill {
            padding: 8px 10px;
            font-size: 12px;
          }
        }

        @media (max-width: 360px) {
          .weatherBox {
            padding: 8px !important;
          }

          .weatherTemp {
            font-size: 26px !important;
          }

          .weatherField {
            font-size: 12px;
          }

          .groupName {
            font-size: 14px;
          }

          .badge, .badgeOk {
            padding: 6px 8px;
            font-size: 12px;
          }
        }
      `}</style>
    </AppShell>
  );
}